<template>
  <div class="sticker-feature-container">
    <VDropdown
      :distance="10"
      placement="top-end"
      :triggers="['click']"
      :auto-hide="true"
      @show="onMenuShow"
      @hide="onMenuHide"
      theme="sticker-menu"
      :shown.sync="isDropdownShown"
    >
      <button
        class="sticker-btn"
        :class="{ 'dropdown-active': isDropdownShown }"
      >
        <img src="@/assets/sticker.svg" class="trigger-icon" alt="stickers" />
      </button>

      <template #popper="{ hide }">
        <div class="sticker-menu">
          <div class="sticker-menu-header">
            <span class="title">Stickers</span>
            <button @click="hide" class="close-btn">
              <img src="@/images/close.svg" class="close-icon" alt="close" />
            </button>
          </div>

          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading sticker packs...</p>
          </div>

          <template v-else>
            <div class="sticker-packs-list">
              <button
                v-for="pack in stickerPacks"
                :key="pack.id"
                class="sticker-pack-preview"
                :class="{
                  active: selectedPack?.id === pack.id,
                  locked: !isPackPurchased(pack.id),
                }"
                @click="selectPack(pack)"
              >
                <img
                  class="sticker-pack-image"
                  :src="pack.image_url"
                  :alt="pack.name"
                />
                <img
                  v-if="!isPackPurchased(pack.id)"
                  src="@/assets/lock.svg"
                  class="lock-icon"
                  alt="locked"
                />
              </button>
            </div>

            <div class="separator"></div>

            <div
              v-if="selectedPack"
              class="pack-content-wrapper"
              :class="{ purchased: isPackPurchased(selectedPack.id) }"
            >
              <template v-if="isPackPurchased(selectedPack.id)">
                <VMenu placement="right" :distance="8">
                  <button
                    class="gift-button"
                    @click="
                      () => {
                        hide();
                        handleGiftClick();
                      }
                    "
                  >
                    <img src="@/assets/gift.svg" alt="gift" />
                    <span>Send this sticker pack like a gift</span>
                  </button>

                  <template #popper>
                    <div class="tooltip-content">
                      It will cost: <b>{{ selectedPack.price }}</b>
                      <img class="coin" src="@/images/coin.svg" alt="coins" />
                    </div>
                  </template>
                </VMenu>

                <div class="sticker-grid">
                  <button
                    v-for="sticker in selectedPack.stickers"
                    :key="sticker.id"
                    class="sticker-item"
                    @click="sendStickerMessage(sticker, hide)"
                  >
                    <img :src="sticker.image_url" :alt="sticker.name" />
                  </button>
                </div>
              </template>

              <template v-else>
                <div class="sticker-grid-preview">
                  <img :src="selectedPack.image_url" :alt="selectedPack.name" />
                </div>
                <p class="buy-prompt-text">
                  Buy this sticker pack to use. Or gift it to your interlocutor.
                </p>

                <p class="price-text">
                  {{ selectedPack.price }}
                  <img class="coin" src="@/images/coin.svg" alt="coins" />
                </p>

                <div class="action-buttons">
                  <button
                    class="button colored"
                    @click="
                      () => {
                        hide();
                        handleGiftClick();
                      }
                    "
                  >
                    <img class="gift-icon" src="@/assets/gift.svg" alt="gift" />
                    Send gift
                  </button>
                  <button
                    class="button bordered-white"
                    @click="
                      () => {
                        hide();
                        handleBuyClick();
                      }
                    "
                  >
                    Buy
                  </button>
                </div>
              </template>
            </div>
          </template>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "StickerMenu",
  components: {},
  props: {
    companionName: {
      type: String,
      required: true,
    },
    topicId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selectedPack: null,
      loading: false,
      isDropdownShown: false,
    };
  },
  computed: {
    ...mapGetters("stickers", ["stickerPacks", "isPackPurchased"]),
  },
  methods: {
    ...mapActions("stickers", [
      "buyStickerPack",
      "giftStickerPack",
      "fetchStickerPacks",
      "sendSticker",
    ]),
    ...mapActions("app", ["showConfirmationModal", "showNotification"]),

    async onMenuShow() {
      this.loading = true;
      try {
        await this.fetchStickerPacks();

        if (!this.selectedPack && this.stickerPacks.length > 0) {
          this.selectedPack = this.stickerPacks[0];
        }
      } catch (error) {
        this.showNotification({
          type: "error",
          message: "Failed to load sticker packs",
        });
      } finally {
        this.loading = false;
      }
    },

    onMenuHide() {},

    selectPack(pack) {
      this.selectedPack = pack;
    },

    async sendStickerMessage(sticker, hideMenu) {
      try {
        await this.sendSticker({
          stickerId: sticker.id,
          topicId: this.topicId,
        });
        hideMenu();
      } catch (error) {
        // Error is already handled in the action
      }
    },

    async handleBuyClick() {
      if (!this.selectedPack) return;

      try {
        await this.buyStickerPack(this.selectedPack.id);
      } catch (error) {
        // Error is already handled in the action
      }
    },

    handleGiftClick() {
      if (!this.selectedPack) return;

      this.showConfirmationModal(`Send a gift to ${this.companionName}?`)
        .then(() => {
          this.giftStickerPack({
            packId: this.selectedPack.id,
            topicId: this.topicId,
            companionName: this.companionName,
          });
        })
        .catch((error) => {
          console.error(
            "StickerMenu: Error during confirmation or gift dispatch",
            error
          );
        });
    },
  },
  created() {
    this.fetchStickerPacks().then(() => {
      if (this.stickerPacks.length > 0) {
        this.selectedPack = this.stickerPacks[0];
      }
    });
  },
};
</script>

<style scoped>
b {
  font-weight: bold;
}
.sticker-feature-container {
  position: relative;
  display: flex;
  align-items: center;
}

.sticker-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  opacity: 1;
}

.sticker-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.trigger-icon {
  width: 22px;
  height: 22px;
  opacity: 0.7;
  transition: opacity 0.2s, filter 0.2s;
}

.sticker-btn:hover .trigger-icon {
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(69%) sepia(62%) saturate(5829%)
    hue-rotate(274deg) brightness(98%) contrast(89%);
}

/* New style for when the dropdown is active */
.sticker-btn.dropdown-active .trigger-icon {
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(69%) sepia(62%) saturate(5829%)
    hue-rotate(274deg) brightness(98%) contrast(89%);
}

.sticker-menu {
  width: 334px;
  background: #14141a;
  border-radius: 8px;
  color: white;
  display: flex;
  flex-direction: column;
  padding: 12px 14px 30px 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.sticker-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.title {
  font-family: "Roboto Mono", monospace;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #ffffff;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.close-icon {
  width: 20px;
  height: 20px;
  display: block;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid #ed59e3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.sticker-packs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.sticker-pack-preview {
  position: relative;
  width: 36px;
  height: 36px;
  padding: 4px;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid transparent;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s;
}

.sticker-pack-preview.locked {
  opacity: 0.6;
}

.sticker-pack-preview:hover {
  border-color: rgba(237, 89, 227, 0.5);
}

.sticker-pack-preview.active {
  border-color: #ed59e3;
  opacity: 1;
}

.sticker-pack-preview img {
  object-fit: contain;
}

.sticker-pack-image {
  width: 100%;
  height: 100%;
}

.lock-icon {
  position: absolute;
  top: 0;
  right: 0;
  width: 14px;
  height: 14px;
  transform: translate(50%, -50%);
  background: #14141a;
  border-radius: 50%;
  padding: 2px;
}

.separator {
  width: 100%;
  height: 1px;
  border-top: 1px solid #1d1d24;
  margin: 20px 0 14px;
}

.pack-content-wrapper {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin-top: 19px;
}

.pack-content-wrapper.purchased {
  margin-top: 0;
}

.gift-button {
  display: flex;
  align-items: center;
  gap: 27px;
  border: 1px solid #ffffff;
  border-radius: 8px;
  padding: 0 12px;
  height: 36px;
  width: 100%;
  background: transparent;
  color: white;
  font-family: "Roboto";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.gift-button:hover {
  opacity: 1;
  background: linear-gradient(#ed59e3, #eb5f71);
  border: 1px solid #fff;
}

.gift-button img {
  width: 16px;
  height: 16px;
}

.tooltip-content {
  background-color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-family: "Roboto";
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
}

.tooltip-content .coin {
  width: 30px;
  height: 30px;
}

.sticker-grid {
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(3, 86px);
  gap: 20px;
  justify-content: center;
}

.sticker-item {
  padding: 0;
  width: 86px;
  height: 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.sticker-item:hover {
  background: #1a1a20;
  opacity: 1;
}

.sticker-item img {
  max-width: 66px;
  max-height: 66px;
  object-fit: contain;
}

.sticker-grid-preview {
  margin: 0 auto;
  margin-bottom: 12px;
}

.sticker-grid-preview img {
  width: auto;
  height: 28px;
}

.sticker-item-preview {
  width: 86px;
  height: 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0e0e12;
  border-radius: 8px;
}

.sticker-item-preview img {
  max-width: 66px;
  max-height: 66px;
  object-fit: contain;
}

.buy-prompt-text {
  max-width: 242px;
  margin: 0 auto;
  line-height: 24px;
  font-family: "Roboto Mono", monospace;
  font-weight: 400;
  text-align: center;
  margin-bottom: 44px;
  font-size: 14px;
  color: #ffffff;
}

.price-text {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 31px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  color: #ffffff;
}

.price-text .coin {
  width: 30px;
  height: 30px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 26px;
}

.button {
  height: 42px;
  min-width: 124px;
  gap: 12px;
}

.button .gift-icon {
  width: 20px;
  height: 20px;
}
</style>

<style>
.v-popper--theme-sticker-menu .v-popper_popper {
  z-index: 9 !important;
}
.v-popper--theme-sticker-menu .v-popper__inner {
  border: none !important;
}

.v-popper--theme-sticker-menu .v-popper__arrow-container {
  display: none !important;
}
</style>
